import { Directive, ElementRef, HostListener, Input, ViewContainerRef } from "@angular/core";

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BehaviorSubject,
  distinctUntilChanged,
  switchMap,
  timer,
  map,
  of,
  filter,
  tap,
  take
} from "rxjs";
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from "@angular/cdk/portal";
import { ProjectItemThumbnailCardHoverComponent } from "../components/project-item-thumbnail-card-hover/project-item-thumbnail-card-hover.component";
import { Project } from "../../_core/state/project";
import { fromEvent } from "rxjs";
import { Router } from "@angular/router";
import { ProjectActionsService } from "../../pages/projects/services/project-actions.service";
import { GlobalQuery, GlobalService } from "../../_core/state/global";
import { SessionQuery } from "../../_core/state/session";

const STATIC_HOVER_DELAY_IN_MS = 500;

function isNodeBlacklisted(root: EventTarget, currentElement: EventTarget) {
  // For reusability, consider making  blacklisting attribute to blacklist nodes over chcecking classList

  if (root instanceof Element && currentElement instanceof Element) {
    if (root === currentElement) {
      return false;
    }

    if (currentElement.classList.contains('mat-tooltip-trigger')) {
      return true;
    }
    return isNodeBlacklisted(root, currentElement.parentElement);
  } else {
    return false;
  }
}

@Directive({
  selector: '[appProjectItemThumbnailCardHover]',
})
export class ProjectItemThumbnailCardHoverDirective {
  constructor(
      private readonly overlay: Overlay,
      private readonly vcr: ViewContainerRef,
	  private readonly router: Router,
	  private readonly projectActionsService: ProjectActionsService,
      private readonly globalQuery: GlobalQuery,
      private readonly sessionQuery: SessionQuery,
      private readonly globalService: GlobalService
  ) {}

  isHovering$ = new BehaviorSubject<boolean>(false);
  mousePosition$ = new BehaviorSubject<MouseEvent>(null);

  @Input() project: Project;

  private _prevent_open: boolean = false;

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    this._prevent_open = true;

    this.router.navigate(['/project', this.project.id]).then(() => {
      setTimeout(() => {
        this._prevent_open = false;
      }, 500);
    });

    if (this.overlayRef$?.value) {
      this.overlayRef$.value.dispose();
      this.overlayRef$.next(null);
    }
  }

  @HostListener('mouseenter')
  mouseEnter() {
    this.isHovering$.next(true);
  }

  @HostListener('mouseleave')
  mouseLeave() {
    this.isHovering$.next(false);
  }

  @HostListener('mousemove', ['$event'])
  mouseMove(event: MouseEvent) {
    this.mousePosition$.next(event);
  }
  /*
    There can be other child elements (like tooltips), we should ignore
  */
  isHoveringValidTarget$ = this.isHovering$.pipe(
      switchMap(isHoveringContainer => isHoveringContainer ? this.mousePosition$.pipe(
          filter(Boolean),
          map(event => !isNodeBlacklisted(event.currentTarget, event.target)),
      ) : of (false)),
      distinctUntilChanged()
  )

  /*
    Hovering over an item is not enough, it should be intentional hover without mouse movement for xxx ms.
  */
  delayedStaticHover$ = this.isHoveringValidTarget$.pipe(
      switchMap((isHovering) => isHovering ?
          this.mousePosition$.pipe(switchMap(() => timer(STATIC_HOVER_DELAY_IN_MS).pipe(map(() => true)))) :
          of(false)
      ),
      distinctUntilChanged(),
      takeUntilDestroyed()
  );

  getViewportWidth() {
    return document.documentElement.clientWidth || 0;
  }

  overlayRef$ = new BehaviorSubject<OverlayRef>(null);

  mouseLeaveOverlay$ = this.overlayRef$.pipe(
      switchMap(overlayRef => overlayRef ? fromEvent<MouseEvent>(overlayRef.overlayElement, 'mouseleave') : of(null)),
      filter(event => event !== null),
      switchMap(() => this.isHovering$),
      filter(isHovering => !isHovering),
      take(1)
  );


  manageOverlayVisibility$ = this.overlayRef$.pipe(
      switchMap(overlayRef => {
        if (overlayRef) {
          return this.mouseLeaveOverlay$.pipe(
              tap(() => {
                this.overlayRef$.value.dispose();
                this.overlayRef$.next(null);
              })
          );
        } else {
          return this.delayedStaticHover$.pipe(
              filter(Boolean),
              switchMap(() => this.mousePosition$.pipe(
                  filter(Boolean),
                  take(1)
              )),
              tap((mouseEvent: MouseEvent) => {
                /** check for application processes that should prevent showing overlay **/
                if  (this._prevent_open || this.projectActionsService.projectDeleting.getValue() || this.projectActionsService.projectCreating.getValue()) {
                  return;
                }

                /** check if application has showing overlay enabled **/
                this.globalQuery.authenticatedSettings$.pipe(take(1)).subscribe(settings => {
                  if (settings?.disableProjectHoverOverlay) {
                    return;
                  }

                  const target = {
                    getBoundingClientRect: () => ({
                      left: mouseEvent.clientX,
                      right: mouseEvent.clientX,
                      top: mouseEvent.clientY,
                      bottom:  mouseEvent.clientY,
                      width: 0,
                      height: 0,
                    })
                  };
                  const targetRef = new ElementRef(target);

                  const prefersLeft = mouseEvent.clientX > this.getViewportWidth() / 2;

                  const overlayRef = this.overlay.create({
                    scrollStrategy: this.overlay.scrollStrategies.reposition(),
                    positionStrategy:  this.overlay.position()
                        .flexibleConnectedTo(targetRef)
                        .withPositions([{
                          originX:  prefersLeft ? 'end' : 'start',
                          overlayX: prefersLeft ? 'end' : 'start',
                          originY: 'center',
                          overlayY: 'center',
                        }]),
                    hasBackdrop: true,
                    backdropClass: 'cdk-overlay-transparent-backdrop',
                  });
                  this.overlayRef$.next(overlayRef);

                  const componentPortal = new ComponentPortal(ProjectItemThumbnailCardHoverComponent, this.vcr);

                  const portal = this.overlayRef$.value.attach(componentPortal);

                  //  this is added because portal stayed open when redirecting via navigation
                  portal.instance.closeOverlay.subscribe(() => {
                    this.overlayRef$.value.dispose();
                    this.overlayRef$.next(null);
                  });

                  portal.instance.project = this.project;
                })
              })
          );
        }
      }),
      takeUntilDestroyed()
  );


  	ngOnInit() {
		this.manageOverlayVisibility$.subscribe();
	}
}
